import React, { useEffect, useState } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail
} from "firebase/auth";
import { useToast } from "./ui/ToastProvider";
import { mapFirebaseError } from "../utils/userErrors";

const auth = getAuth();
const provider = new GoogleAuthProvider();

export default function AuthScreen() {
  const { showToast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMode, setAuthMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetNotice, setResetNotice] = useState("");
  const isSignUp = authMode === "signup";
  const isResetMode = authMode === "reset";

  useEffect(() => {
    document.body.className = "theme-ocean";
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search || "");
    if (params.get("mode") === "reset") {
      setAuthMode("reset");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading || isResetMode) return;
    if (!email.trim()) {
      setFormError("Email is required.");
      return;
    }
    if (!password.trim()) {
      setFormError("Password is required.");
      return;
    }
    if (isSignUp && password.trim().length < 6) {
      setFormError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    setFormError("");
    setResetNotice("");
    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setFormError(mapFirebaseError(err, "Unable to sign in. Please try again."));
    }
    setLoading(false);
  };

  const googleLogin = async () => {
    if (loading) return;
    setLoading(true);
    setFormError("");
    setResetNotice("");
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      setFormError(mapFirebaseError(err, "Google sign-in failed. Please try again."));
    }
    setLoading(false);
  };

  const resetPassword = async () => {
    if (!email.trim()) {
      setFormError("Enter your account email to send a password reset link.");
      return;
    }
    setResettingPassword(true);
    setFormError("");
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetNotice("Password reset email sent.");
      showToast({ type: "success", message: "Password reset email sent." });
    } catch (error) {
      setFormError(mapFirebaseError(error, "Could not send password reset email."));
    } finally {
      setResettingPassword(false);
    }
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    if (loading || resettingPassword) return;
    await resetPassword();
  };

  return (
    <div className="auth-screen">
      <div className="glass auth-shell">
        <div className="auth-left">
          <h1>FamilySync</h1>
          <p className="auth-subtitle">
            Organize chores, approvals, and rewards in one calm dashboard.
          </p>
          <ul className="auth-highlights">
            <li>Assign chores in seconds</li>
            <li>Kids see only what they need</li>
            <li>Clear approvals + clarifications</li>
          </ul>
        </div>
        <div className="auth-right">
          <div className="auth-card">
            <h2>
              {isResetMode
                ? "Reset password"
                : isSignUp
                ? "Create your account"
                : "Welcome back"}
            </h2>
            <p className="auth-helper">
              {isResetMode
                ? "Enter your account email and we’ll send a reset link."
                : isSignUp
                ? "Start a family board in minutes."
                : "Sign in to keep everyone on track."}
            </p>

            <form onSubmit={isResetMode ? submitResetPassword : submit}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              {!isResetMode && (
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              )}
              {isSignUp && (
                <small style={{ opacity: 0.7, display: "block", marginBottom: 8 }}>
                  Use at least 6 characters.
                </small>
              )}
              {formError && (
                <div className="pin-error" role="alert" style={{ marginBottom: 10 }}>
                  {formError}
                </div>
              )}
              {resetNotice && (
                <div className="success-message" style={{ textAlign: "left", marginBottom: 10 }}>
                  {resetNotice}
                </div>
              )}

              <button
                className="btn-nav kiosk-btn"
                style={{ width: "100%" }}
                disabled={loading || (isResetMode && resettingPassword)}
              >
                {isResetMode
                  ? resettingPassword
                    ? "Sending reset..."
                    : "Send reset email"
                  : loading
                  ? "Processing..."
                  : isSignUp
                  ? "Create Account"
                  : "Login"}
              </button>
            </form>

            {!isResetMode && (
              <button
                className="btn-nav"
                style={{ width: "100%", marginTop: 10 }}
                onClick={googleLogin}
                disabled={loading}
              >
                Continue with Google
              </button>
            )}

            {!isSignUp && !isResetMode && (
              <button
                className="btn-nav"
                style={{ width: "100%" }}
                onClick={() => {
                  setFormError("");
                  setResetNotice("");
                  setAuthMode("reset");
                }}
                disabled={loading}
                type="button"
              >
                Forgot password
              </button>
            )}

            {isResetMode ? (
              <button
                className="auth-toggle"
                onClick={() => {
                  setFormError("");
                  setResetNotice("");
                  setAuthMode("login");
                }}
              >
                Back to login
              </button>
            ) : (
              <button
                className="auth-toggle"
                onClick={() => {
                  setFormError("");
                  setResetNotice("");
                  setAuthMode(isSignUp ? "login" : "signup");
                }}
              >
                {isSignUp
                  ? "Already have an account? Login"
                  : "Need an account? Sign up"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
